/**
 * Timezone-aware date and time formatting.
 *
 * Every timestamp the user sees, and every date bound sent to the API, goes
 * through this module. Two rules make the whole thing consistent:
 *
 *  1. The API speaks UTC instants. DynamoDB stores ISO-8601 strings and the
 *     date GSIs are partitioned by UTC calendar date.
 *  2. The user reads and picks dates in *their* timezone.
 *
 * Everything here therefore converts explicitly at the boundary. The previous
 * helpers used the browser's implicit locale methods (`getFullYear()`,
 * `toLocaleString()`), which cannot honour a user who has chosen a timezone
 * other than the one their laptop is set to — and gave no way to state which
 * timezone a rendered timestamp was actually in.
 *
 * This matters beyond cosmetics for an audit tool: an event at 09:00 on 7 Sep
 * Sydney time is 23:00 on 6 Sep UTC, so a naive "filter by date" puts it in the
 * wrong day, and an evidence export can disagree with the screen it was taken
 * from.
 */

export const AUTO_TIMEZONE = "auto";

export type DateFormatId = "iso" | "dmy" | "mdy" | "medium";
export type TimeFormatId = "24h" | "12h";

/**
 * The timezone the browser reports.
 *
 * Falls back to UTC rather than throwing: some locked-down environments return
 * undefined, and rendering everything in UTC with a visible label is far better
 * than a blank page.
 */
export function detectTimezone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone) return zone;
  } catch {
    /* fall through */
  }
  return "UTC";
}

/** Resolve the "auto" sentinel to a concrete IANA zone name. */
export function resolveTimezone(preference: string | undefined | null): string {
  if (!preference || preference === AUTO_TIMEZONE) return detectTimezone();
  if (!isValidTimezone(preference)) {
    // A stored zone can become invalid if the user's browser is older than the
    // zone (e.g. a recent split). Degrade to the detected zone instead of
    // throwing inside a render.
    return detectTimezone();
  }
  return preference;
}

export function isValidTimezone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The UTC offset of `zone` at `instant`, in minutes east of UTC.
 *
 * Computed from the formatted parts rather than a fixed table so that daylight
 * saving is handled correctly for the specific instant. A fixed offset would be
 * wrong for half the year in Sydney, Auckland, London and most of the US, and
 * wrong at exactly the wrong moments — the DST changeover days an auditor is
 * most likely to query.
 */
export function offsetMinutes(zone: string, instant: Date = new Date()): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");

  // Interpret the wall-clock reading in `zone` as if it were UTC, then compare.
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    // Intl renders midnight as hour 24 in some engines.
    get("hour") % 24,
    get("minute"),
    get("second")
  );
  return Math.round((asUtc - instant.getTime()) / 60000);
}

/** Format `zone`'s current offset as "+10:00" for display next to a timestamp. */
export function offsetLabel(zone: string, instant: Date = new Date()): string {
  const total = offsetMinutes(zone, instant);
  const sign = total < 0 ? "-" : "+";
  const abs = Math.abs(total);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `${sign}${hh}:${mm}`;
}

/**
 * A short label for the zone, e.g. "AEST" — or the offset when the runtime has
 * no abbreviation for it.
 */
export function timezoneAbbreviation(zone: string, instant: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "short",
    }).formatToParts(instant);
    const name = parts.find((p) => p.type === "timeZoneName")?.value;
    // Some engines return "GMT+10" rather than an abbreviation, which is fine.
    if (name) return name;
  } catch {
    /* fall through */
  }
  return `UTC${offsetLabel(zone, instant)}`;
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * Parse a timestamp from the API.
 *
 * The backend is inconsistent about offsets: some collectors write
 * `2026-09-07T04:00:00+00:00` and others `2026-09-07T04:00:00`. A bare string
 * is parsed by `new Date()` as *local* time in every browser, which silently
 * shifts those values by the viewer's offset. Anything without an explicit
 * offset or trailing Z is therefore treated as UTC, matching what the
 * collectors actually mean.
 *
 * Composite DynamoDB sort keys are also accepted. The activity table's sort
 * key is a timestamp with a discriminator appended — `{iso}#{activityId}`,
 * `{iso}#snap#{reportId}#{n}`, `{iso}#git#{sha}#{n}` — needed to keep writes
 * idempotent and to stop several changes to one report in one run collapsing
 * onto the same key. It is still the only timestamp on the row, so it is what
 * every activity table renders, and passing it to `new Date()` yields
 * "Invalid Date".
 */
export function parseInstant(value: string | null | undefined): Date | null {
  if (!value) return null;

  // Split before trimming so a stray space around the separator cannot
  // survive into the timestamp.
  const trimmed = value.trim().split("#")[0].trim();
  if (!trimmed) return null;

  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const looksLikeDateTime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(trimmed);
  const normalised = looksLikeDateTime && !hasZone ? `${trimmed.replace(" ", "T")}Z` : trimmed;

  const parsed = new Date(normalised);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const DATE_STYLES: Record<DateFormatId, Intl.DateTimeFormatOptions> = {
  iso: { year: "numeric", month: "2-digit", day: "2-digit" },
  dmy: { day: "2-digit", month: "2-digit", year: "numeric" },
  mdy: { month: "2-digit", day: "2-digit", year: "numeric" },
  medium: { day: "numeric", month: "short", year: "numeric" },
};

export interface FormatContext {
  timezone: string;
  dateFormat: DateFormatId;
  timeFormat: TimeFormatId;
}

/** The placeholder shown for a missing or unparseable value. */
export const EMPTY_DISPLAY = "—";

function partsOf(
  instant: Date,
  zone: string,
  options: Intl.DateTimeFormatOptions
): Record<string, string> {
  const parts = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: zone }).formatToParts(
    instant
  );
  const out: Record<string, string> = {};
  for (const part of parts) out[part.type] = part.value;
  return out;
}

/** Format the date portion of an instant in the user's timezone. */
export function formatDate(
  value: string | Date | null | undefined,
  ctx: FormatContext
): string {
  const instant = value instanceof Date ? value : parseInstant(value);
  if (!instant) return EMPTY_DISPLAY;

  if (ctx.dateFormat === "iso") {
    // Build ISO explicitly; Intl has no ISO date style and locale ordering
    // varies by engine.
    const p = partsOf(instant, ctx.timezone, DATE_STYLES.iso);
    return `${p.year}-${p.month}-${p.day}`;
  }
  return new Intl.DateTimeFormat("en-GB", {
    ...DATE_STYLES[ctx.dateFormat],
    timeZone: ctx.timezone,
  }).format(instant);
}

/** Format the time portion of an instant in the user's timezone. */
export function formatTime(
  value: string | Date | null | undefined,
  ctx: FormatContext,
  withSeconds = false
): string {
  const instant = value instanceof Date ? value : parseInstant(value);
  if (!instant) return EMPTY_DISPLAY;

  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    ...(withSeconds ? { second: "2-digit" } : {}),
    hour12: ctx.timeFormat === "12h",
    timeZone: ctx.timezone,
  }).format(instant);
}

/** Format a full timestamp in the user's timezone. */
export function formatDateTime(
  value: string | Date | null | undefined,
  ctx: FormatContext,
  withSeconds = false
): string {
  const instant = value instanceof Date ? value : parseInstant(value);
  if (!instant) return EMPTY_DISPLAY;
  return `${formatDate(instant, ctx)} ${formatTime(instant, ctx, withSeconds)}`;
}

/**
 * Format a timestamp with an explicit timezone label.
 *
 * Used wherever a value is evidence rather than decoration — table cells that
 * get exported, detail modals, and anything an auditor may screenshot — so a
 * reader can never mistake which zone a time is in.
 */
export function formatDateTimeWithZone(
  value: string | Date | null | undefined,
  ctx: FormatContext,
  withSeconds = false
): string {
  const instant = value instanceof Date ? value : parseInstant(value);
  if (!instant) return EMPTY_DISPLAY;
  return `${formatDateTime(instant, ctx, withSeconds)} ${timezoneAbbreviation(
    ctx.timezone,
    instant
  )}`;
}

/** Relative time, e.g. "3 minutes ago". Falls back to an absolute date past a week. */
export function formatRelative(
  value: string | Date | null | undefined,
  ctx: FormatContext,
  now: Date = new Date()
): string {
  const instant = value instanceof Date ? value : parseInstant(value);
  if (!instant) return EMPTY_DISPLAY;

  const deltaSeconds = Math.round((instant.getTime() - now.getTime()) / 1000);
  const absSeconds = Math.abs(deltaSeconds);

  if (absSeconds < 45) return "just now";
  if (absSeconds > 7 * 86400) return formatDate(instant, ctx);

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const divisions: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, "second"],
    [3600, "minute"],
    [86400, "hour"],
    [7 * 86400, "day"],
  ];
  let unit: Intl.RelativeTimeFormatUnit = "second";
  let divisor = 1;
  for (const [limit, candidate] of divisions) {
    if (absSeconds < limit) break;
    divisor = limit;
    unit = candidate;
  }
  // `divisions` pairs each threshold with the unit one step *up*, so the value
  // is scaled by the threshold that was passed.
  return rtf.format(Math.round(deltaSeconds / divisor), unit);
}

// ---------------------------------------------------------------------------
// Local calendar dates (for pickers and filter defaults)
// ---------------------------------------------------------------------------

/**
 * The YYYY-MM-DD calendar date of `instant` as seen in `zone`.
 *
 * Never use `toISOString().slice(0, 10)` for this: that is the UTC date, which
 * is yesterday for a Sydney user for the first ten hours of every day.
 */
export function calendarDateIn(zone: string, instant: Date = new Date()): string {
  const p = partsOf(instant, zone, DATE_STYLES.iso);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Today's calendar date in `zone`, as YYYY-MM-DD. */
export function todayIn(zone: string): string {
  return calendarDateIn(zone, new Date());
}

/** The calendar date `n` days before today in `zone`, as YYYY-MM-DD. */
export function daysAgoIn(zone: string, n: number): string {
  const base = new Date();
  base.setUTCDate(base.getUTCDate() - n);
  return calendarDateIn(zone, base);
}

// ---------------------------------------------------------------------------
// UTC calendar dates (for the date-window parameters the API takes)
// ---------------------------------------------------------------------------
//
// `date_from` and `date_to` are not instants and not local dates. Every
// endpoint that accepts them uses the value directly as a DynamoDB partition
// key — `Key("eventDate").eq(date_str)`, an exact equality with no range or
// tolerance — and the collectors write those partitions from the UTC calendar
// date of each event. So a date that is off by one does not return a slightly
// different window; it reads a completely different partition.
//
// These windows used to be computed from the *browser's* local date, which was
// wrong twice over. It ignored the timezone the user had configured, so the
// preference changed what they read but never what the application asked for.
// And a local calendar date is not a UTC calendar date, so the window was
// additionally shifted by the offset — a Sydney "last 7 days" quietly began at
// 10:00 on the day before the one shown in the picker, and for the last four
// hours of a New York working day, "today" was a partition that did not exist
// yet and the evening's events were simply absent with nothing to say so.
//
// Named rather than written as todayIn("UTC") at twenty call sites, so that
// the reason is stated once and the call sites are greppable.

/** Today's UTC calendar date, as YYYY-MM-DD. */
export function utcToday(): string {
  return calendarDateIn("UTC");
}

/** The UTC calendar date `n` days before today, as YYYY-MM-DD. */
export function utcDaysAgo(n: number): string {
  const base = new Date();
  base.setUTCDate(base.getUTCDate() - n);
  return calendarDateIn("UTC", base);
}

/**
 * Convert a picked calendar date into the UTC instant at which that day
 * *starts* in `zone`.
 *
 * The offset is resolved at the target date, not today, so a range spanning a
 * daylight-saving boundary is still correct.
 */
export function startOfDayUtc(dateStr: string, zone: string): Date {
  return zonedWallClockToUtc(dateStr, "00:00:00", zone);
}

/** The UTC instant at which the day *after* `dateStr` starts in `zone`. */
export function endOfDayUtcExclusive(dateStr: string, zone: string): Date {
  const start = startOfDayUtc(dateStr, zone);
  // Add 24h then re-resolve, so a DST shift does not produce a 23h or 25h day.
  const nextDay = new Date(start.getTime() + 24 * 3600 * 1000);
  return startOfDayUtc(calendarDateIn(zone, nextDay), zone);
}

/**
 * Interpret a wall-clock reading in `zone` as a UTC instant.
 *
 * Two passes are needed: the first guesses using the offset at the naive
 * instant, and the second corrects it using the offset that actually applies at
 * the guessed instant. Without the second pass, times within an hour of a DST
 * transition land on the wrong side of it.
 */
export function zonedWallClockToUtc(dateStr: string, timeStr: string, zone: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh = 0, mm = 0, ss = 0] = timeStr.split(":").map(Number);
  const naiveUtc = Date.UTC(y, (m ?? 1) - 1, d ?? 1, hh, mm, ss);

  const firstGuess = new Date(naiveUtc - offsetMinutes(zone, new Date(naiveUtc)) * 60000);
  const corrected = new Date(naiveUtc - offsetMinutes(zone, firstGuess) * 60000);
  return corrected;
}

/**
 * Turn a local date range from a picker into the UTC instant bounds the API
 * expects.
 *
 * `to` is inclusive as the user means it — picking 7 Sep includes everything
 * that happened on 7 Sep in their timezone — so the returned upper bound is the
 * exclusive start of 8 Sep.
 */
export function localRangeToUtcBounds(
  from: string | undefined,
  to: string | undefined,
  zone: string
): { fromUtc?: string; toUtc?: string } {
  return {
    fromUtc: from ? startOfDayUtc(from, zone).toISOString() : undefined,
    toUtc: to ? endOfDayUtcExclusive(to, zone).toISOString() : undefined,
  };
}

/**
 * The UTC calendar dates a local date range touches.
 *
 * The backend's date GSIs are keyed on UTC dates, so a local day in a non-UTC
 * zone spans two UTC partitions. Callers that must query by UTC date use this
 * to widen the request, then filter precisely on the returned timestamps.
 */
export function utcDatesCovering(from: string, to: string, zone: string): string[] {
  const startUtc = startOfDayUtc(from, zone);
  const endUtc = endOfDayUtcExclusive(to, zone);

  const dates: string[] = [];
  const cursor = new Date(
    Date.UTC(startUtc.getUTCFullYear(), startUtc.getUTCMonth(), startUtc.getUTCDate())
  );
  while (cursor.getTime() < endUtc.getTime()) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}
